/**
 * OriginRail — context-aware quick-answer chat in a floating right rail.
 *
 * Scope split between two flavours of Origin:
 *   - OriginRail (this file)    — fast, conversational, streaming. 2–6 sentence
 *                                  answers. No agent loop, no proposals,
 *                                  no cost beyond a single Claude call.
 *                                  Used for "tell me about her", "what was
 *                                  life like in Bleiswijk 1880", etc.
 *   - OriginResearchPanel       — the deep V0.1 research agent on PersonPage.
 *                                  Multi-step, sources, proposals into the
 *                                  Suggestions Inbox. Launched explicitly
 *                                  when the user wants deep research; rail
 *                                  has a "Run deep research →" CTA linking
 *                                  to it when a focal person is set.
 *
 * Behaviour:
 *   - Renders as an overlay on the right (no layout reservation).
 *   - Collapsible to a vertical pill via `collapsed` + `onCollapsedChange`.
 *   - Streams `/api/ai/chat` with tree_id + focal_person_id + active_view.
 *   - Conversation history kept in component state (last 10 turns).
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api.js'
import { useAuthStore } from '../../stores/auth.js'
import './OriginRail.css'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

export default function OriginRail({
  treeId, focalPerson, activeView,
  collapsed: collapsedProp, onCollapsedChange,
}) {
  const [collapsedLocal, setCollapsedLocal] = useState(true)
  const collapsed     = collapsedProp ?? collapsedLocal
  const setCollapsed  = onCollapsedChange ?? setCollapsedLocal

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Ask me anything about anyone in your tree — living or historical. I\'m fast and I\'ll point out gaps as I go. For deep sourced research on one person, open their profile and run the full Origin investigation.',
    },
  ])
  const [input, setInput]         = useState('')
  const [streaming, setStreaming] = useState(false)
  const [usage, setUsage]         = useState(null)
  const token        = useAuthStore(s => s.token)
  const navigate     = useNavigate()
  const bottomRef    = useRef(null)
  const abortRef     = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Pull today's quick-chat usage on mount. Paid tiers get
  // `{ quota: null }` so the footer shows "Unlimited".
  useEffect(() => {
    api.get('/ai/chat-usage').then(setUsage).catch(() => {})
  }, [])

  function refreshUsage() {
    api.get('/ai/chat-usage').then(setUsage).catch(() => {})
  }

  // When the user clicks a different person, clear the chat so prompts
  // don't reference an out-of-context prior turn. The greeting message
  // stays.
  useEffect(() => {
    setMessages(prev => prev.length > 1 ? [prev[0]] : prev)
  }, [focalPerson?.id])

  async function send(text) {
    const prompt = (text ?? input).trim()
    if (!prompt || streaming) return
    setInput('')
    const userMsg      = { role: 'user',      content: prompt }
    const assistantMsg = { role: 'assistant', content: '' }
    const history      = messages.filter(m => m.role !== 'assistant' || m.content).slice(-10)

    setMessages(prev => [...prev, userMsg, assistantMsg])
    setStreaming(true)

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const res = await fetch(`${API_BASE}/ai/chat`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: prompt,
          tree_id: treeId,
          focal_person_id: focalPerson?.id || null,
          active_view: activeView,
          conversation_history: history,
        }),
      })

      if (!res.ok) {
        const errBody = await res.text().catch(() => '')
        let parsed = null
        try { parsed = JSON.parse(errBody) } catch {}
        if (res.status === 402 && parsed?.error?.code === 'TIER_LIMIT') {
          appendToLastAssistant(parsed.error.message || 'Daily Origin chat limit reached. Upgrade to Plus for unlimited.')
        } else {
          appendToLastAssistant(`Something went wrong (${res.status}). ${errBody.slice(0, 120)}`)
        }
        refreshUsage()
        return
      }

      const reader  = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop()
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.text) appendToLastAssistant(data.text)
          } catch { /* ignore malformed SSE chunk */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        appendToLastAssistant(`\n\n(connection error: ${err.message || 'unknown'})`)
      }
    } finally {
      setStreaming(false)
      abortRef.current = null
      refreshUsage()
    }
  }

  function appendToLastAssistant(delta) {
    setMessages(prev => {
      const next = [...prev]
      const last = next[next.length - 1]
      if (last && last.role === 'assistant') {
        next[next.length - 1] = { ...last, content: last.content + delta }
      }
      return next
    })
  }

  function cancel() {
    abortRef.current?.abort()
  }

  function openDeepResearch() {
    if (!focalPerson?.id || !treeId) return
    navigate(`/tree/${treeId}/person/${focalPerson.id}`)
  }

  const suggestions = buildSuggestions({ activeView, focalPerson })

  if (collapsed) {
    return (
      <button
        className="origin-rail origin-rail--collapsed"
        onClick={() => setCollapsed(false)}
        aria-label="Open Origin AI"
      >
        <span aria-hidden className="origin-rail__icon">🔍</span>
        <span className="origin-rail__vertical">Origin</span>
        {streaming && <span className="origin-rail__pulse" aria-label="thinking" />}
      </button>
    )
  }

  return (
    <aside className="origin-rail" role="complementary" aria-label="Origin AI">
      <header className="origin-rail__header">
        <div className="origin-rail__title">
          <span aria-hidden className="origin-rail__icon">🔍</span>
          <span>Origin</span>
        </div>
        <button
          className="origin-rail__collapse"
          onClick={() => setCollapsed(true)}
          aria-label="Collapse Origin"
        >›</button>
      </header>

      <div className="origin-rail__context">
        {contextLabel({ activeView, focalPerson })}
        {focalPerson && (
          <button
            className="origin-rail__deep-cta"
            onClick={openDeepResearch}
            title="Open the full research agent on this person's profile"
          >
            Run deep research →
          </button>
        )}
      </div>

      <div className="origin-rail__chat">
        {messages.map((m, i) => (
          <div key={i} className={`origin-rail__msg origin-rail__msg--${m.role}`}>
            {m.content || (streaming && i === messages.length - 1 ? <span className="origin-rail__caret">▍</span> : null)}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {!streaming && messages.length <= 1 && suggestions.length > 0 && (
        <div className="origin-rail__suggestions">
          <div className="origin-rail__section-title">Try asking</div>
          {suggestions.map(s => (
            <button
              key={s}
              className="origin-rail__suggestion"
              onClick={() => send(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        className="origin-rail__form"
        onSubmit={e => { e.preventDefault(); send() }}
      >
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={streaming ? 'Thinking…' : 'Ask a quick question…'}
          disabled={streaming}
        />
        {streaming ? (
          <button type="button" className="origin-rail__send origin-rail__send--cancel" onClick={cancel} aria-label="Stop">×</button>
        ) : (
          <button type="submit" className="origin-rail__send" disabled={!input.trim()} aria-label="Send">↑</button>
        )}
      </form>

      <footer className="origin-rail__footer">
        {usage && usage.quota != null ? (
          <span className={usage.remaining === 0 ? 'origin-rail__quota--out' : ''}>
            {usage.remaining > 0
              ? <>{usage.remaining} of {usage.quota} messages left today · <a href="/billing">Upgrade</a> for unlimited</>
              : <>Daily limit reached · <a href="/billing">Upgrade to Plus</a> for unlimited</>}
          </span>
        ) : (
          <>Quick answers & gap-spotting · for sourced research, open a profile</>
        )}
      </footer>
    </aside>
  )
}

// ── Context-aware quick prompts (conversational, not research-y) ────────────

function buildSuggestions({ activeView, focalPerson }) {
  const name = focalPerson
    ? (focalPerson.preferred_name || focalPerson.first_name || `${focalPerson.first_name || ''} ${focalPerson.last_name || ''}`.trim() || 'this person')
    : null

  if (focalPerson) {
    return [
      `What's missing about ${name}?`,
      `Tell me briefly about ${name}.`,
      `What was life like in ${focalPerson.birth_place || 'their place'} around ${(String(focalPerson.birth_date || '').slice(0, 4)) || 'their era'}?`,
      `What records or family members could help fill the gaps?`,
    ]
  }

  switch (activeView) {
    case 'directory':
      return [
        'Which persons in this tree have the most gaps?',
        'Who is missing a birth date or place?',
        'Are there obvious duplicates I should merge?',
      ]
    case 'places':
      return [
        'Which place appears most in this tree?',
        'Are there migration patterns across generations?',
        'Which towns are worth checking the civil register for?',
      ]
    case 'timeline':
      return [
        'Where are the biggest gaps in this tree\'s timeline?',
        'Which major historical events overlap with these lifespans?',
      ]
    case 'network':
      return [
        'Are there isolated clusters not connected to the main lineage?',
        'Which connections look incomplete?',
      ]
    case 'worlds':
      return [
        'Which capsule would best fit someone in this tree?',
      ]
    default:
      return [
        'Where are the biggest gaps in this tree right now?',
        'Give me a one-paragraph summary of this tree.',
        'Which person looks most worth researching next?',
      ]
  }
}

function contextLabel({ activeView, focalPerson }) {
  if (focalPerson) {
    const name = focalPerson.preferred_name
      || `${focalPerson.first_name || ''} ${focalPerson.last_name || ''}`.trim()
      || 'Unknown'
    return <><strong>{name}</strong></>
  }
  return <>Tree · {VIEW_LABEL[activeView] || 'overview'}</>
}

const VIEW_LABEL = {
  tree:      'Family Tree',
  directory: 'Directory',
  pedigree:  'Pedigree',
  timeline:  'Timeline',
  network:   'Network',
  places:    'Places',
  worlds:    'Worlds',
  discover:  'Discovery',
  library:   'Library',
}
